import { models } from "types/api/viewModels.ts";
import {
	AddressLine1Text,
	ConfirmationNumberText,
	CabinetCountText,
	DesignerNumberText,
	DoorStyleText,
	FinishText,
	ItemsText,
	OrderDateText,
	OrderNumberText,
	PONumberText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { formatDateToMMDDYYYY } from "utils/date.ts";
import { displayDesigner, getOrderTypeDisplayLabel, isCampaignOrder, isMerchOrCampaignOrder } from "utils/order.ts";
import { getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { formatFinishDescription } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { OrderCategory, OrderStatus, OrderType } from "types/declarations/enums.ts";
import { ORDER_STATUS_REASON_TOOLTIPS, ORDER_STATUS_TOOLTIPS } from "constants/orderCardStatusTooltips.ts";
import { OrderChip } from "components/OrderSummaryCard/components/OrderSummaryShip/OrderSummaryChip.tsx";

interface OrderDetail {
	label: string;
	value: string | number | null | undefined;
	testId: string;
}

export const buildOrderDetails = (order: models["OrderViewModel"]): OrderDetail[] => {
	const usesMerchOrCampaignLayout = isMerchOrCampaignOrder(order.orderType);

	return [
		{
			label: OrderDateText,
			value: order.orderDate ? formatDateToMMDDYYYY(new Date(order.orderDate)) : UnavailableDataPlaceholderText,
			testId: "single-card-order-date"
		},
		{ label: PONumberText, value: order.poNumber, testId: "single-card-po-number" },
		{ label: ConfirmationNumberText, value: order.confirmationNumber, testId: "single-card-confirmation-number" },
		{ label: OrderNumberText, value: order.orderNumber, testId: "single-card-order-number" },
		...(usesMerchOrCampaignLayout
			? [
					isCampaignOrder(order.orderType)
						? {
								label: AddressLine1Text,
								value: order.shippingDetails?.shipToAddress?.line1,
								testId: "single-card-address-line1"
							}
						: {
								label: ItemsText,
								value: order.counts?.totalQuantityOrdered ?? 0,
								testId: "single-card-items-count"
							}
				]
			: [
					{
						label: DesignerNumberText,
						value: displayDesigner(order.designer),
						testId: "single-card-designer"
					},
					{
						label: DoorStyleText,
						value: getSingleOrderGlobal(order)?.style?.description,
						testId: "single-card-door-style"
					},
					{
						label: FinishText,
						value: formatFinishDescription(
							getSingleOrderGlobal(order)?.finish,
							order.orderType === OrderType.CUSTOM_COLOR
						),
						testId: "single-card-finish"
					},
					{
						label: CabinetCountText,
						value: order.counts?.cabinetCount ?? 0,
						testId: "single-card-cabinetCount"
					}
				])
	];
};

export const buildOrderChips = (order: models["OrderViewModel"]): OrderChip[] =>
	[
		order.orderType ? { label: order.orderType, toolTip: null } : undefined,
		order.status && order.status !== OrderStatus.HOLD && order.status !== OrderStatus.UNKNOWN
			? {
					label: order.status,
					toolTip: ORDER_STATUS_TOOLTIPS.find((s) => s.status === order.status)?.description
				}
			: undefined,
		order.statusReason
			? {
					label: order.statusReason,
					toolTip: ORDER_STATUS_REASON_TOOLTIPS.find((s) => s.status === order.statusReason)?.description
				}
			: undefined,
		order.orderCategory === OrderCategory.STOCK ? { label: order.orderCategory, toolTip: null } : undefined
	].filter(Boolean) as OrderChip[];
